import { cn } from '@simplywallst/ui-core/next';

import { Button } from '@simplywallst/ui-core/next';
import EditSolid from '@simplywallst/ui-core/icons/EditSolid';
import PlusSolid from '@simplywallst/ui-core/icons/PlusSolid';
import {
  ROUTE_NARRATIVE_UPDATE_CREATE_EDIT,
  ROUTE_PUBLIC_NARRATIVE_EDIT,
} from '@/constants/routes';
import { getPath } from '@/router/utils';
import { useTracking } from 'react-tracking';
import { useSnackbar } from '@/features/snackbar';
import { NarrativeSetFairValue } from '@/pages/Narrative/components/NarrativeSetFairValue';
import { useCallback } from 'react';
import type { FragmentOf } from '@/utilities/gql';
import type { NARRATIVE_CARD_ACTIONS_FRAGMENT } from './narrativeCardActionsFragment';
import { isAfter } from 'date-fns';
import { useFlag } from '@simplywallst/unleash';
import { useUserProfile } from '@/hooks/useUserProfile';

type Props = {
  narrative: FragmentOf<typeof NARRATIVE_CARD_ACTIONS_FRAGMENT>;
  returnPath?: string;
  onError?: () => void;
};

export const NarrativeCardActions = ({
  narrative,
  returnPath,
  onError,
}: Props) => {
  const { publicId: currentUserPublicId } = useUserProfile();
  const {
    id: narrativeId,
    isPrimary,
    owner,
    companyId,
    valuation,
    draftUpdate,
    latestPublishedUpdate,
  } = narrative;
  const { type: valuationType } = valuation ?? {};
  const { publicId } = owner;
  const isMyNarrative = currentUserPublicId === publicId;
  const { trackEvent } = useTracking();
  const { addSnackbar } = useSnackbar();
  const { enabled: isUpdatesViewEnabled } = useFlag(
    'mono-narrative-updates-view'
  );

  const handleError = useCallback(() => {
    if (typeof onError === 'function') {
      onError();
      return;
    }
    addSnackbar({
      message: 'Failed to select fair value. Please try again.',
      type: 'negative',
      lifeTime: 10000,
    });
  }, [addSnackbar, onError]);

  const getMyNarrativeActionMeta = () => {
    if (!isMyNarrative)
      return {
        href: null,
        content: null,
      };

    const isEditableWithinTimelock =
      latestPublishedUpdate?.willLockAt &&
      isAfter(new Date(latestPublishedUpdate.willLockAt), new Date());

    const draftUpdateId = draftUpdate?.id;
    const hasDraftUpdate = !!draftUpdateId;
    const isNarrativeDraft = !latestPublishedUpdate;

    if (
      !isUpdatesViewEnabled ||
      isNarrativeDraft ||
      (!hasDraftUpdate && isEditableWithinTimelock)
    )
      return {
        content: (
          <>
            <EditSolid className="!mr-1" />
            Edit Narrative
          </>
        ),
        href: getPath({
          type: ROUTE_PUBLIC_NARRATIVE_EDIT,
          payload: {
            id: narrativeId,
            query: {
              company: companyId ? companyId.toLowerCase() : undefined,
              returnPath,
            },
          },
        }),
      };

    const hasOpenDraftOrEditableUpdate =
      draftUpdateId || isEditableWithinTimelock;

    return {
      content: (
        <>
          <PlusSolid className="!mr-1" />
          {hasOpenDraftOrEditableUpdate ? 'Continue Update' : 'Update'}
        </>
      ),
      href: getPath({
        type: ROUTE_NARRATIVE_UPDATE_CREATE_EDIT,
        payload: {
          companyid: companyId,
          query: {
            returnPath,
          },
        },
      }),
    };
  };

  const { content, href } = getMyNarrativeActionMeta();

  return (
    <div
      className={cn(
        'grid gap-x1',
        isMyNarrative ? 'grid-cols-2' : 'grid-cols-1'
      )}
    >
      {href && content && (
        <Button
          variant="outline"
          size="large"
          href={href}
          className="!px-x0 text-center"
          onClick={() => {
            trackEvent({
              action: 'click',
              target: 'edit-narrative',
              valuationType: 'user_defined',
            });
          }}
        >
          {content}
        </Button>
      )}
      <NarrativeSetFairValue
        companyId={companyId}
        narrativeId={narrativeId}
        isPrimary={isPrimary}
        onError={handleError}
        valuationType={valuationType}
      />
    </div>
  );
};
